import React, { useState, useRef, useLayoutEffect } from 'react';
import { makeStyles, Tooltip, ClickAwayListener } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { useCardTableStyles } from './styles';
import { useExperienceSearchContext } from 'contexts/ExperienceSearchContext';
import { useToggleStarMutation } from 'utils/wemble-api';

interface ExperienceStats {
  matterCount: number;
  clientCount: number;
  totalMinutes: number;
  coStaffingDays?: number;
}

interface ExperienceEntry {
  dimension: string;
  key: string;
  label?: string;
  userId?: string;
  profilePicture?: string;
  starred?: boolean;
  allTime: ExperienceStats;
  pastYear: ExperienceStats;
  pastMonth: ExperienceStats;
}

type Scope = 'allTime' | 'pastYear' | 'pastMonth';

interface Props {
  userId: string;
  entries: ExperienceEntry[];
  dataAvailableSince?: string | null;
  userName?: string;
  canEditStars?: boolean;
  /** When false, dimensions that would reveal aggregate hours (country,
   *  partner, workType, practiceArea, industry, teamMember) hide their
   *  absolute "X h" number but keep the bar + matter/client counts.
   *  Client keeps hours (many clients, low aggregation risk) and peer shows
   *  days, not hours. Mirrors the existing matter-hours permission gate. */
  showHours?: boolean;
}

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginTop: 10,
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  scopeToggle: {
    display: 'flex',
    gap: 4,
    marginTop: 4,
  },
  scopeButton: {
    fontSize: 11,
    fontWeight: 500,
    padding: '3px 8px',
    borderRadius: 6,
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.15s, color 0.15s, transform 0.1s',
    userSelect: 'none' as const,
    '&:active': {
      transform: 'scale(0.93)',
    },
  },
  scopeActive: {
    backgroundColor: '#38566B',
    color: 'white',
  },
  scopeInactive: {
    backgroundColor: '#f0f0f0',
    color: '#888',
    '&:hover': {
      backgroundColor: '#e4e4e4',
    },
  },
  dimensionWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  dimensionLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    color: theme.palette.gray?.g2 || '#888',
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  infoButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    fontSize: 10,
    marginBottom: 2,
    fontWeight: 600,
    textTransform: 'lowercase' as const,
    color: '#bbb',
    border: '1.5px solid #ccc',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
    background: 'none',
    padding: 0,
    lineHeight: 1,
    position: 'relative' as const,
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: -4,
      right: -4,
      bottom: -4,
      left: -4,
    },
    '@media (hover: hover)': {
      '&:hover': {
        color: '#888',
        borderColor: '#aaa',
      },
    },
  },
  card: {
    backgroundColor: theme.palette.gray?.g7 || '#f5f5f5',
    borderRadius: 8,
    padding: '10px 14px',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: '1fr 80px auto',
    columnGap: 6,
    rowGap: 3,
    alignItems: 'center',
    alignContent: 'start',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr 50px auto',
    },
  },
  cardWithStars: {
    padding: '10px 6px 10px 14px',
    gridTemplateColumns: '1fr 80px auto 22px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr 50px auto 22px',
    },
  },
  flag: {
    width: 16,
    height: 12,
    borderRadius: 1,
    boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)',
    flexShrink: 0,
  },
  avatar: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: '#ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 8,
    fontWeight: 700,
    color: '#666',
    flexShrink: 0,
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
  },
  rowLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text?.primary || '#333',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  rowLabelZero: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.gray?.g2 || '#999',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  barContainer: {
    height: 14,
    borderRadius: 4,
    backgroundColor: 'rgba(0,0,0,0.06)',
    overflow: 'hidden',
    cursor: 'default',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
    minWidth: 2,
  },
  rowValue: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.gray?.g2 || '#888',
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
  },
  star: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  starEditable: {
    cursor: 'pointer',
    transition: 'transform 0.15s',
    '&:hover': {
      transform: 'scale(1.2)',
    },
  },
  starActive: {
    color: '#F5A623',
    fontSize: 19,
  },
  starInactive: {
    color: '#ddd',
    fontSize: 19,
  },
  starInactiveEditable: {
    color: '#ddd',
    fontSize: 19,
    '&:hover': {
      color: '#F5A623',
    },
  },
  showMore: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.gray?.g2 || '#888',
    cursor: 'pointer',
    textAlign: 'center' as const,
    padding: '4px 0 2px',
    gridColumn: '1 / -1',
    '&:hover': {
      color: theme.palette.text?.primary || '#333',
    },
  },
  footer: {
    fontSize: 11,
    color: '#bbb',
    marginTop: 8,
    textAlign: 'center' as const,
  },
}));

import { DIMENSION_CONFIG, DIMENSION_ORDER } from '../experienceDimensions';
const SCOPE_LABELS: Record<Scope, string> = { pastMonth: '3 months', pastYear: '1 year', allTime: 'All time' };

const countryNames = typeof (Intl as any).DisplayNames === 'function'
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;
const getCountryName = (code: string) => { try { return countryNames?.of(code) || code; } catch (e) { return code; } };

const formatHours = (minutes: number) => {
  if (minutes === 0) return '—';
  const hours = Math.round(minutes / 60);
  if (hours === 0) return '< 1 h';
  if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k h`;
  return `${hours} h`;
};

const getInitials = (name: string) => {
  const parts = name.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const getStats = (entry: ExperienceEntry, scope: Scope): ExperienceStats => entry[scope] || { matterCount: 0, clientCount: 0, totalMinutes: 0 };

// Dimensions where the absolute hours number is hidden from users who don't
// have the hours permission. For simplicity and consistency we hide it on all
// dimensions except peer (which shows days, not hours) when the viewer can't
// see hours.
const SENSITIVE_HOURS_DIMENSIONS = new Set(['country', 'partner', 'client', 'workType', 'practiceArea', 'industry', 'teamMember']);

const ExperienceRow: React.FC<{
  entry: ExperienceEntry;
  scope: Scope;
  maxValue: number;
  color: string;
  showFlag?: boolean;
  showAvatar?: boolean;
  isPeer?: boolean;
  showStar?: boolean;
  canToggleStar?: boolean;
  onToggleStar?: () => void;
  userName?: string;
  showHours?: boolean;
}> = ({ entry, scope, maxValue, color, showFlag, showAvatar, isPeer, showStar, canToggleStar, onToggleStar, userName, showHours = true }) => {
  const classes = useStyles();
  const stats = getStats(entry, scope);
  const isZero = isPeer ? !stats.coStaffingDays : !stats.totalMinutes;
  const barValue = isPeer ? (stats.coStaffingDays || 0) : stats.totalMinutes;
  const hideHours = !showHours && SENSITIVE_HOURS_DIMENSIONS.has(entry.dimension);
  const matterSuffix = stats.matterCount === 1 ? 'matter' : 'matters';
  const displayValue = isPeer
    ? `${stats.coStaffingDays || 0} days`
    : hideHours
      ? `${stats.matterCount} ${matterSuffix}`
      : formatHours(stats.totalMinutes);
  const tooltip = isPeer
    ? `${stats.matterCount} shared matters · ${stats.coStaffingDays || 0} days working together`
    : isZero
      ? (hideHours ? 'No matters' : 'No hours recorded')
      : hideHours
        ? `${stats.matterCount} ${matterSuffix}${stats.clientCount ? ` · ${stats.clientCount} clients` : ''}`
        : `${stats.matterCount} matters${stats.clientCount ? ` · ${stats.clientCount} clients` : ''} · ${formatHours(stats.totalMinutes)}`;

  const name = userName || 'This person';
  const label = entry.dimension === 'country' ? getCountryName(entry.key) : (entry.label || entry.key);
  const starTooltip = entry.starred
    ? `${name} has indicated a preference for ${label}`
    : '';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {entry.key.startsWith('__conf_') ? (
          <LockOutlinedIcon style={{ fontSize: 14, opacity: 0.4, flexShrink: 0 }} />
        ) : showFlag && entry.key ? (
          <span className={`fi fi-${entry.key.toLowerCase()} ${classes.flag}`} />
        ) : null}
        {showAvatar && (
          <div className={classes.avatar}>
            {entry.profilePicture ? (
              <img
                alt={entry.label || ''}
                src={'https://d378wr66tg5qg7.cloudfront.net/' + entry.profilePicture}
              />
            ) : (
              getInitials(entry.label || entry.key)
            )}
          </div>
        )}
        <div className={isZero ? classes.rowLabelZero : classes.rowLabel}>
          {label}
        </div>
      </div>
      <Tooltip title={tooltip} arrow placement="top">
        <div className={classes.barContainer}>
          {!isZero && (
            <div
              className={classes.barFill}
              style={{
                width: `${Math.min((barValue / maxValue) * 100, 100)}%`,
                backgroundColor: color,
                opacity: 0.6,
              }}
            />
          )}
        </div>
      </Tooltip>
      <div className={classes.rowValue}>{displayValue}</div>
      {showStar && (
        <Tooltip title={starTooltip} arrow placement="right">
          <div
            className={`${classes.star}${canToggleStar ? ` ${classes.starEditable}` : ''}`}
            onClick={canToggleStar ? (e) => { e.stopPropagation(); onToggleStar?.(); } : undefined}
          >
            {entry.starred ? (
              <StarIcon className={classes.starActive} />
            ) : (
              <StarBorderIcon className={canToggleStar ? classes.starInactiveEditable : classes.starInactive} />
            )}
          </div>
        </Tooltip>
      )}
    </>
  );
};

const ExperienceProfile: React.FC<Props> = ({ userId, entries, dataAvailableSince, userName, canEditStars, showHours = true }) => {
  const classes = useStyles();
  const sectionClasses = useCardTableStyles();
  const { experienceScope: searchScope } = useExperienceSearchContext();
  const [toggleStar] = useToggleStarMutation();
  const showStars = true;
  const savedScope = localStorage.getItem('wemble_experience_scope') as Scope | null;
  const [chosenScope, setScope] = useState<Scope>((searchScope as Scope) || savedScope || 'pastYear');

  const handleScopeChange = (s: Scope) => {
    setScope(s);
    localStorage.setItem('wemble_experience_scope', s);
  };
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [infoHover, setInfoHover] = useState<string | null>(null);
  const [infoPinned, setInfoPinned] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [fitCounts, setFitCounts] = useState<Record<string, number>>({});

  // Measure non-expanded cards to auto-fill available space
  useLayoutEffect(() => {
    const timer = requestAnimationFrame(() => {
      const ROW_HEIGHT = 23;
      const PADDING = 20;
      const SHOW_MORE_HEIGHT = 24;
      const newCounts: Record<string, number> = {};
      for (const [dim, el] of Object.entries(cardRefs.current)) {
        if (!el || expanded.has(dim)) continue;
        const available = el.clientHeight - PADDING - SHOW_MORE_HEIGHT;
        newCounts[dim] = Math.max(1, Math.floor(available / ROW_HEIGHT));
      }
      setFitCounts(newCounts);
    });
    return () => cancelAnimationFrame(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded.size]);

  if (!entries || entries.length === 0) return null;

  // Group by dimension — filter partner/peer/teamMember to only Wemble users (with resolved userId)
  const personDimensions = ['partner', 'peer', 'teamMember'];
  const byDimension = new Map<string, ExperienceEntry[]>();
  for (const entry of entries) {
    if (personDimensions.includes(entry.dimension) && !entry.userId) continue;
    const existing = byDimension.get(entry.dimension) || [];
    existing.push(entry);
    byDimension.set(entry.dimension, existing);
  }

  // Nothing in the chosen period (e.g. 3 months for someone whose client work is older): show the next wider period
  // that has something, rather than no section and no period buttons. The saved choice stays as it is.
  const hasData = (s: Scope) => Array.from(byDimension.entries()).some(([dim, dimEntries]) => DIMENSION_ORDER.includes(dim)
    && dimEntries.some(e => (dim === 'peer' ? getStats(e, s).coStaffingDays || 0 : getStats(e, s).totalMinutes) > 0));
  const widths: Scope[] = ['pastMonth', 'pastYear', 'allTime'];
  const scope = widths.slice(widths.indexOf(chosenScope)).find(hasData) || chosenScope;

  // Sort by current scope's primary metric (coStaffingDays for peers, totalMinutes for others)
  Array.from(byDimension.entries()).forEach(([dim, dimEntries]) => {
    dimEntries.sort((a, b) => {
      const aStats = getStats(a, scope);
      const bStats = getStats(b, scope);
      if (dim === 'peer') return (bStats.coStaffingDays || 0) - (aStats.coStaffingDays || 0);
      return bStats.totalMinutes - aStats.totalMinutes;
    });
  });

  // Determine if this person is primarily a lead or contributor in the current scope
  // by comparing total hours as team lead vs hours under other leads
  const teamMemberHours = (byDimension.get('teamMember') || []).reduce((sum, e) => sum + (getStats(e, scope).totalMinutes || 0), 0);
  const partnerHours = (byDimension.get('partner') || []).reduce((sum, e) => sum + (getStats(e, scope).totalMinutes || 0), 0);
  const isPrimarilyLead = teamMemberHours > partnerHours;

  const visibleDimensions = DIMENSION_ORDER.filter(dim => {
    // Show teamMember only if primarily a lead, partner only if primarily a contributor
    if (dim === 'teamMember' && !isPrimarilyLead) return false;
    if (dim === 'partner' && isPrimarilyLead) return false;

    const dimEntries = byDimension.get(dim);
    if (!dimEntries || dimEntries.length === 0) return false;
    return dimEntries.some(e => {
      const s = getStats(e, scope);
      return dim === 'peer' ? (s.coStaffingDays || 0) > 0 : s.totalMinutes > 0;
    });
  });

  if (visibleDimensions.length === 0) return null;

  const dataSinceText = dataAvailableSince
    ? (() => {
        const d = new Date(dataAvailableSince);
        const years = Math.round((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
        if (years >= 2) return `Experience data available for the past ${years} years`;
        const months = Math.round((Date.now() - d.getTime()) / (30.44 * 24 * 60 * 60 * 1000));
        if (months >= 2) return `Experience data available for the past ${months} months`;
        return `Experience data available since ${d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
      })()
    : null;

  return (
    <div className={sectionClasses.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Experience</h2>
        <div className={classes.scopeToggle}>
          {(['pastMonth', 'pastYear', 'allTime'] as Scope[]).map(s => (
            <button
              key={s}
              className={`${classes.scopeButton} ${scope === s ? classes.scopeActive : classes.scopeInactive}`}
              onClick={() => handleScopeChange(s)}
            >
              {SCOPE_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.grid}>
        {visibleDimensions.map((dim, idx) => {
          const dimEntries = byDimension.get(dim)!;
          const config = DIMENSION_CONFIG[dim];
          if (!config) return null;

          // When odd number of dimensions, make the last right-column item span 2 rows
          const isOddCount = visibleDimensions.length % 2 === 1;
          const isLastRightColumn = isOddCount && idx === visibleDimensions.length - 2;

          const title = config.title;
          const description = config.description(userName || 'this person');
          const isPeer = config.useCoStaffingDays;

          // Filter out entries with zero in the current scope
          const nonZero = dimEntries.filter(e => {
            const s = getStats(e, scope);
            return isPeer ? (s.coStaffingDays || 0) > 0 : s.totalMinutes > 0;
          });

          if (nonZero.length === 0) return null;

          const maxValue = Math.max(
            ...nonZero.map(e => {
              const s = getStats(e, scope);
              return isPeer ? (s.coStaffingDays || 0) : s.totalMinutes;
            }),
            1,
          );

          const isExpanded = expanded.has(dim);
          // When spanning 2 rows, show enough items to roughly fill the combined height
          // of the two left-column neighbors (their defaultVisible + labels + show-more links)
          // Only auto-fill the spanning card (odd layout, last right column)
          // Other cards use their fixed defaultVisible to avoid collapse issues
          const measuredFit = isLastRightColumn && !isExpanded ? fitCounts[dim] : undefined;
          const defaultCount = Math.max(config.defaultVisible, measuredFit || 0);
          const maxItems = isExpanded ? (config.maxExpanded || nonZero.length) : Math.min(defaultCount, nonZero.length);
          const visible = nonZero.slice(0, maxItems);
          const hasMore = nonZero.length > defaultCount;

          return (
            <div key={dim} className={classes.dimensionWrapper} style={isLastRightColumn ? { gridRow: 'span 2' } : undefined}>
              <div className={classes.dimensionLabel}>
                <span>{title}</span>
                <ClickAwayListener onClickAway={() => { if (infoPinned === dim) setInfoPinned(null); }}>
                  <Tooltip
                    title={description}
                    arrow
                    placement="top"
                    open={infoPinned === dim || infoHover === dim}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    interactive
                    PopperProps={{ modifiers: { offset: { enabled: true, offset: '0, -4' } } }}
                  >
                    <span
                      className={classes.infoButton}
                      onClick={() => setInfoPinned(prev => prev === dim ? null : dim)}
                      onMouseEnter={() => setInfoHover(dim)}
                      onMouseLeave={() => setInfoHover(null)}
                    >
                      i
                    </span>
                  </Tooltip>
                </ClickAwayListener>
              </div>
              <div
                className={`${classes.card}${showStars ? ` ${classes.cardWithStars}` : ''}`}
                ref={el => { cardRefs.current[dim] = el; }}
              >
                {visible.map(entry => (
                  <ExperienceRow
                    key={entry.key}
                    entry={entry}
                    scope={scope}
                    maxValue={maxValue}
                    color={config.color}
                    showFlag={config.showFlag}
                    showAvatar={config.showAvatar}
                    isPeer={isPeer}
                    showStar={showStars}
                    canToggleStar={canEditStars}
                    onToggleStar={() => toggleStar({
                      userId,
                      body: { dimension: entry.dimension, key: entry.key, label: entry.label || entry.key },
                    })}
                    userName={userName}
                    showHours={showHours}
                  />
                ))}
                {hasMore && (
                  <div
                    className={classes.showMore}
                    onClick={() => setExpanded(prev => {
                      const next = new Set(prev);
                      if (next.has(dim)) next.delete(dim);
                      else next.add(dim);
                      return next;
                    })}
                  >
                    {isExpanded ? 'Show less' : config.maxExpanded
                      ? `Show top ${Math.min(config.maxExpanded, nonZero.length)}`
                      : `${nonZero.length - visible.length} more`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dataSinceText && (
        <div className={classes.footer}>
          {dataSinceText}
        </div>
      )}
    </div>
  );
};

export default ExperienceProfile;
